<template>
  <div class="bg-white rounded-lg shadow-sm p-8 md:p-12">
    <div class="flex justify-between items-center mb-8">
      <div>
        <h3 class="text-2xl md:text-3xl font-marketing font-bold text-primary">Image Banner</h3>
        <p class="text-sm font-sans text-gray-600 mt-2">Preview renders at the top of this admin page</p>
      </div>
      <button @click="handleCancel" class="text-gray-600 hover:text-gray-800 font-marketing font-semibold">
        Cancel
      </button>
    </div>

    <div class="space-y-6">
      <form @submit.prevent="saveBanner" class="space-y-6">
        <div>
          <label class="block text-sm font-marketing font-semibold text-primary mb-2">Title (Identifier) *</label>
          <input v-model="formData.title" type="text" required
            class="w-full px-4 py-3 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-sans"
            placeholder="e.g., Summer Sale Banner" />
          <p class="text-xs text-gray-500 mt-1 font-sans">This is for identification purposes only and will not be
            displayed on the website.</p>
        </div>

        <div>
          <label class="block text-sm font-marketing font-semibold text-primary mb-2">Banner Image *</label>
          <div class="space-y-3">
            <div class="flex gap-3">
              <input ref="bannerFileInput" type="file" accept="image/*,.heic,.heif" @change="handleBannerFileSelect"
                class="hidden" />
              <button type="button" @click="() => bannerFileInput?.click()"
                class="px-6 py-3 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors font-marketing font-semibold">
                Upload Image
              </button>
              <input v-model="formData.image_url" type="url"
                class="flex-1 px-4 py-3 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-sans"
                placeholder="Or enter image URL" />
            </div>
            <div v-if="bannerImagePreview || (formData.image_url && !uploadingBanner)" class="mt-3 space-y-3">
              <div class="flex gap-3 items-start">
                <div :style="getImageContainerStyle()"
                  class="border border-creme-300 overflow-hidden bg-white flex items-center justify-center">
                  <img :src="bannerImagePreview || formData.image_url" alt="Banner Preview" :style="getImageStyle()"
                    class="w-full h-full object-cover" />
                </div>
                <div class="flex flex-col gap-2">
                  <button type="button" @click="() => loadImageForCropping()"
                    class="px-4 py-2 bg-accent text-white rounded-md hover:bg-green-700 transition-colors font-marketing font-semibold text-sm">
                    Crop Image
                  </button>
                  <button type="button" @click="clearBannerImage"
                    class="px-4 py-2 text-sm text-red-600 hover:text-red-700 font-marketing">
                    Remove Image
                  </button>
                </div>
              </div>
            </div>
            <div v-if="uploadingBanner" class="text-sm text-gray-600 font-sans">
              Uploading...
            </div>
          </div>
        </div>

        <div v-if="formData.image_url || bannerImagePreview" class="space-y-4 border-t border-gray-200 pt-6">
          <div>
            <label class="block text-sm font-marketing font-semibold text-primary mb-3">Image Display</label>
            <p class="text-xs text-gray-500 mb-3 font-sans">The image will be displayed in a square box that takes up
              half the banner width. The image will be stretched to fill the square.</p>
          </div>

          <div>
            <label class="block text-sm font-marketing font-semibold text-primary mb-2">Subheading (Optional)</label>
            <div class="border border-gray-300 rounded-md">
              <RichTextEditor ref="subheadingEditor" :content="formData.text_subheading"
                @update:content="formData.text_subheading = $event" placeholder="Enter subheading text..." />
            </div>
          </div>

          <div>
            <label class="block text-sm font-marketing font-semibold text-primary mb-2">Content Text</label>
            <div class="border border-gray-300 rounded-md">
              <RichTextEditor ref="contentEditor" :content="formData.text_overlay"
                @update:content="formData.text_overlay = $event" placeholder="Enter main content text..." />
            </div>
          </div>

          <div v-if="formData.text_overlay || formData.text_subheading" class="space-y-4">
            <div>
              <label class="block text-sm font-marketing font-semibold text-primary mb-2">Image Position</label>
              <select v-model="formData.text_position"
                class="w-full px-4 py-3 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-sans">
                <option value="left">Image on Left, Content on Right</option>
                <option value="right">Image on Right, Content on Left</option>
              </select>
              <p class="text-xs text-gray-500 mt-2 font-sans">Choose which side the image appears on (RHS = Right Hand
                Side)</p>
            </div>

            <div>
              <label class="block text-sm font-marketing font-semibold text-primary mb-2">Text Font</label>
              <select v-model="formData.text_font"
                class="w-full px-4 py-3 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-sans">
                <option value="font-marketing">EB Garamond (Marketing)</option>
                <option value="font-sans">Georgia (Sans)</option>
                <option value="font-serif">EB Garamond (Serif)</option>
                <option value="font-email">Georgia (Email)</option>
              </select>
              <p class="text-xs text-gray-500 mt-2 font-sans">Choose the font family for the content text</p>
            </div>

            <div>
              <label class="block text-sm font-marketing font-semibold text-primary mb-2">Text Size</label>
              <select v-model="formData.text_size_class"
                class="w-full px-4 py-3 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-sans">
                <option value="text-base xl:text-lg">Base / Large</option>
                <option value="text-lg xl:text-xl">Large / Extra Large</option>
                <option value="text-xl xl:text-2xl">Extra Large / 2XL</option>
                <option value="text-2xl xl:text-3xl">2XL / 3XL</option>
              </select>
              <p class="text-xs text-gray-500 mt-2 font-sans">Adjust desktop and XL font sizes for banner content</p>
            </div>

            <div>
              <label class="block text-sm font-marketing font-semibold text-primary mb-3">Text Color</label>
              <ColorPicker v-model="formData.text_color" />
              <p class="text-xs text-gray-600 mt-2 font-sans">Selected color: {{ formData.text_color || '#111111' }}</p>
            </div>

            <div>
              <label class="block text-sm font-marketing font-semibold text-primary mb-3">Content Background
                (Optional)</label>
              <ColorPicker v-model="formData.text_background" />
              <p class="text-xs text-gray-600 mt-2 font-sans">Selected color: {{ formData.text_background || 'None' }}
              </p>
            </div>
          </div>

        </div>

        <div>
          <label class="block text-sm font-marketing font-semibold text-primary mb-2">Link URL</label>
          <input v-model="formData.link_url" type="url"
            class="w-full px-4 py-3 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-sans"
            placeholder="https://example.com" />
        </div>

        <div>
          <label class="block text-sm font-marketing font-semibold text-primary mb-2">Link Text</label>
          <input v-model="formData.link_text" type="text"
            class="w-full px-4 py-3 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-sans"
            placeholder="Learn More" />
        </div>

        <div>
          <label class="block text-sm font-marketing font-semibold text-primary mb-2">Position *</label>
          <select v-model="formData.position" required
            class="w-full px-4 py-3 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-sans">
            <option value="top">Top</option>
            <option value="bottom">Bottom</option>
          </select>
        </div>

        <div>
          <label class="block text-sm font-marketing font-semibold text-primary mb-3">Pages</label>
          <div class="max-h-48 overflow-y-auto border border-gray-300 rounded-md p-4 bg-white">
            <label class="flex items-center gap-3 mb-3 p-2 hover:bg-creme-50 rounded cursor-pointer transition-colors">
              <input type="checkbox" :checked="!formData.pages || formData.pages.length === 0"
                @change="formData.pages = []"
                class="w-4 h-4 bg-white border-gray-300 text-gray-900 focus:ring-gray-400 focus:ring-2 rounded" />
              <span class="text-sm font-sans">All Pages</span>
            </label>
            <div class="border-t border-gray-200 my-3"></div>
            <label v-for="page in availablePages" :key="page.value"
              class="flex items-center gap-3 mb-2 p-2 hover:bg-creme-50 rounded cursor-pointer transition-colors">
              <input type="checkbox" :value="page.value" v-model="formData.pages"
                class="w-4 h-4 bg-white border-gray-300 text-gray-900 focus:ring-gray-400 focus:ring-2 rounded" />
              <span class="text-sm font-sans">{{ page.label }}</span>
            </label>
          </div>
          <p class="text-xs text-gray-600 mt-2 font-sans">
            Selected: {{ (!formData.pages || formData.pages.length === 0) ? 'All Pages' : formData.pages.join(', ') }}
          </p>
        </div>

        <div>
          <label class="flex items-center gap-3">
            <input v-model="formData.is_active" type="checkbox"
              class="w-4 h-4 bg-white border-gray-300 text-gray-900 focus:ring-gray-400 focus:ring-2 rounded" />
            <span class="text-sm font-marketing font-semibold text-primary">Active</span>
          </label>
        </div>

        <div class="flex gap-3 pt-4 border-t border-gray-200">
          <button type="submit"
            class="px-8 py-3 bg-accent text-white rounded-md hover:bg-green-700 transition-colors font-marketing font-semibold">
            {{ editingBanner ? 'Update Banner' : 'Create Banner' }}
          </button>
          <button type="button" @click="handleCancel"
            class="px-8 py-3 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors font-marketing font-semibold">
            Cancel
          </button>
        </div>
      </form>
    </div>

    <BaseModal v-model="showCropModal" content-class="max-w-4xl max-h-[90vh]" @close="closeCropModal">
      <div class="mb-4">
        <h3 class="text-xl font-marketing font-bold text-primary">Crop Banner Image</h3>
      </div>
      <div v-if="cropImageSrc" class="mb-4 relative" style="height: 700px; width: 100%;">
        <Cropper :key="`cropper-${cropImageSrc}`" ref="cropper" :src="cropImageSrc" :default-boundaries="'fill'"
          :stencil-props="{
            aspectRatio: 1
          }" @change="onCropChange" @ready="onCropperReady" style="width: 100%; height: 100%;" />
      </div>
      <div v-else class="mb-4 flex items-center justify-center" style="height: 700px;">
        <p class="text-gray-500 font-sans">No image selected</p>
      </div>

      <div v-if="cropResult && cropResult.coordinates" class="mb-6 p-4 bg-creme-100 rounded-lg border border-creme-300">
        <h4 class="text-sm font-marketing font-semibold text-primary mb-3">Preview</h4>
        <div class="flex items-center justify-center">
          <div class="flex flex-col items-center">
            <div :style="getPreviewStyle()"
              class="bg-white border-2 border-gray-300 shadow-md mb-2 overflow-hidden flex items-center justify-center">
              <img v-if="previewImageUrl" :src="previewImageUrl" alt="Preview" class="w-full h-full object-cover" />
            </div>
            <p class="text-xs text-gray-500 font-sans mt-1">{{ Math.round(cropResult.coordinates.width) }} × {{
              Math.round(cropResult.coordinates.height) }}px</p>
          </div>
        </div>
      </div>

      <div class="flex gap-3 justify-end">
        <button @click="closeCropModal"
          class="px-6 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors font-marketing font-semibold">
          Cancel
        </button>
        <button @click="applyCrop"
          class="px-6 py-2 bg-accent text-white rounded-md hover:bg-green-700 transition-colors font-marketing font-semibold">
          Apply Crop
        </button>
      </div>
    </BaseModal>
  </div>
</template>

<script setup>
import { ref, watch, onUnmounted, nextTick } from 'vue'
import { adminService } from '../../services/admin'
import ColorPicker from './ColorPicker.vue'
import RichTextEditor from '../common/RichTextEditor.vue'
import { Cropper } from 'vue-advanced-cropper'
import 'vue-advanced-cropper/dist/style.css'
import BaseModal from './BaseModal.vue'
import { API_BASE_URL } from '../../config'
import { convertLegacyQuillContent } from '../../utils/quillConfig'
import { useScrollToTop } from '../../composables/useScrollToTop'

const props = defineProps({
  editingBanner: {
    type: Object,
    default: null
  }
})

const emit = defineEmits(['cancel', 'saved', 'preview-updated'])

const { scrollToTop } = useScrollToTop()

const updatePreview = () => {
  if (formData.value.image_url || bannerImagePreview.value) {
    emit('preview-updated', {
      image_url: bannerImagePreview.value || formData.value.image_url,
      text_overlay: formData.value.text_overlay,
      text_subheading: formData.value.text_subheading,
      text_position: formData.value.text_position,
      text_color: formData.value.text_color,
      text_background: formData.value.text_background,
      text_font: formData.value.text_font,
      text_size_class: formData.value.text_size_class,
      max_height: formData.value.max_height,
      image_dimensions: {
        cropDimensions: imageDimensions.value.cropDimensions || null
      }
    })
  } else {
    emit('preview-updated', null)
  }
}

const handleCancel = () => {
  emit('preview-updated', null)
  emit('cancel')
  scrollToTop()
}

onUnmounted(() => {
  emit('preview-updated', null)
})

const bannerFileInput = ref(null)
const bannerImagePreview = ref('')
const uploadingBanner = ref(false)
const subheadingEditor = ref(null)
const contentEditor = ref(null)
const showCropModal = ref(false)
const cropper = ref(null)
const cropImageSrc = ref('')
const cropResult = ref(null)
const selectedFile = ref(null)
const previewImageUrl = ref('')
const imageNaturalSize = ref({ width: 0, height: 0 })

const imageDimensions = ref({
  cropDimensions: { width: 0, height: 0 }
})

const formData = ref({
  title: '',
  image_url: '',
  link_url: '',
  link_text: '',
  position: 'top',
  pages: [],
  is_active: true,
  text_overlay: convertLegacyQuillContent(''),
  text_subheading: convertLegacyQuillContent(''),
  text_position: 'right',
  max_height: null,
  text_color: '#111111',
  text_background: '',
  text_font: 'font-marketing',
  text_size_class: 'text-xl xl:text-2xl'
})

const availablePages = [
  { value: 'home', label: 'Home' },
  { value: 'books', label: 'Books' },
  { value: 'book', label: 'Book Detail' },
  { value: 'authors', label: 'Authors' },
  { value: 'author', label: 'Author Detail' },
  { value: 'about', label: 'About' },
  { value: 'partners', label: 'Partners' },
  { value: 'contact', label: 'Contact' },
  { value: 'donate', label: 'Donate' },
  { value: 'events', label: 'Events' },
  { value: 'event-detail', label: 'Event Detail' },
  { value: 'careers', label: 'Careers' },
  { value: 'press', label: 'Press' },
  { value: 'submissions', label: 'Submissions' }
]

if (props.editingBanner) {
  formData.value = {
    title: props.editingBanner.title || '',
    image_url: props.editingBanner.image_url || '',
    link_url: props.editingBanner.link_url || '',
    link_text: props.editingBanner.link_text || '',
    position: props.editingBanner.position || 'top',
    pages: Array.isArray(props.editingBanner.pages) ? props.editingBanner.pages : [],
    is_active: props.editingBanner.is_active !== false,
    text_overlay: convertLegacyQuillContent(props.editingBanner.text_overlay || ''),
    text_subheading: convertLegacyQuillContent(props.editingBanner.text_subheading || ''),
    text_position: props.editingBanner.text_position || 'right',
    max_height: props.editingBanner.max_height || null,
    text_color: props.editingBanner.text_color || '#111111',
    text_background: props.editingBanner.text_background || '',
    text_font: props.editingBanner.text_font || 'font-marketing',
    text_size_class: props.editingBanner.text_size_class || 'text-xl xl:text-2xl'
  }
  if (props.editingBanner?.image_dimensions) {
    imageDimensions.value = {
      cropDimensions: props.editingBanner.image_dimensions.cropDimensions || { width: 0, height: 0 }
    }
  } else {
    imageDimensions.value.cropDimensions = { width: 0, height: 0 }
  }
  nextTick(() => {
    if (subheadingEditor.value) {
      subheadingEditor.value.setHTML(formData.value.text_subheading || '')
    }
    if (contentEditor.value) {
      contentEditor.value.setHTML(formData.value.text_overlay || '')
    }
  })
  updatePreview()
}

watch([() => formData.value.image_url, () => formData.value.text_overlay, () => formData.value.text_subheading, () => formData.value.text_position, () => formData.value.text_color, () => formData.value.text_background, () => formData.value.text_font, () => formData.value.text_size_class, () => formData.value.max_height, () => imageDimensions.value.cropDimensions, () => bannerImagePreview.value], () => {
  updatePreview()
}, { deep: true, immediate: false })


const handleBannerFileSelect = async (event) => {
  const file = event.target.files[0]
  if (!file) return

  const isImage = file.type.startsWith('image/') ||
    file.name.toLowerCase().endsWith('.heic') ||
    file.name.toLowerCase().endsWith('.heif')
  if (!isImage) {
    alert('Please select an image file')
    return
  }

  if (file.size > 10 * 1024 * 1024) {
    alert('Image size must be less than 10MB')
    return
  }

  try {
    const { convertHeicForUpload, convertHeicForPreview } = await import('../../utils/heicConverter')
    const convertedFile = await convertHeicForUpload(file)
    selectedFile.value = convertedFile

    const blobUrl = await convertHeicForPreview(file)
    bannerImagePreview.value = blobUrl
    formData.value.image_url = blobUrl
    updatePreview()
  } catch (error) {
    console.error('Failed to convert HEIC:', error)
    selectedFile.value = file
    const blobUrl = URL.createObjectURL(file)
    bannerImagePreview.value = blobUrl
    formData.value.image_url = blobUrl
    updatePreview()
  }
}

const clearBannerImage = () => {
  formData.value.image_url = ''
  bannerImagePreview.value = ''
  imageDimensions.value.cropDimensions = { width: 0, height: 0 }
  if (bannerFileInput.value) {
    bannerFileInput.value.value = ''
  }
  updatePreview()
}

const loadImageForCropping = async () => {
  const imageUrl = bannerImagePreview.value || formData.value.image_url
  if (!imageUrl) {
    alert('Please upload an image or enter an image URL first')
    return
  }

  let blobUrl = bannerImagePreview.value

  if (!blobUrl || !blobUrl.startsWith('blob:')) {
    try {
      let blob
      if (imageUrl.includes('storage.googleapis.com')) {
        const proxyUrl = `${API_BASE_URL}/api/admin/proxy/image?url=${encodeURIComponent(imageUrl)}`
        const response = await fetch(proxyUrl, {
          credentials: 'include'
        })
        if (!response.ok) {
          throw new Error('Failed to load image')
        }
        blob = await response.blob()
      } else {
        const response = await fetch(imageUrl)
        if (!response.ok) {
          throw new Error('Failed to load image')
        }
        blob = await response.blob()
      }

      blobUrl = URL.createObjectURL(blob)
      bannerImagePreview.value = blobUrl
    } catch (error) {
      console.error('Failed to load image for cropping:', error)
      alert('Failed to load image. Please check the image URL or try uploading the image again.')
      return
    }
  }

  cropImageSrc.value = blobUrl
  cropResult.value = null
  selectedFile.value = null
  showCropModal.value = true
  await nextTick()
}

const closeCropModal = () => {
  const fileToUpload = selectedFile.value

  if (cropImageSrc.value && cropImageSrc.value.startsWith('blob:')) {
    URL.revokeObjectURL(cropImageSrc.value)
  }
  showCropModal.value = false
  cropResult.value = null
  cropImageSrc.value = ''
  previewImageUrl.value = ''
  imageNaturalSize.value = { width: 0, height: 0 }

  if (fileToUpload && !formData.value.image_url) {
    bannerImagePreview.value = ''
    if (bannerFileInput.value) {
      bannerFileInput.value.value = ''
    }
  } else if (!formData.value.image_url) {
    bannerImagePreview.value = ''
  }

  selectedFile.value = null
}

const onCropperReady = () => {
  if (cropper.value) {
    const result = cropper.value.getResult()
    if (result?.imageSize) {
      imageNaturalSize.value = {
        width: result.imageSize.width,
        height: result.imageSize.height
      }
    }
  }
}

const onCropChange = ({ coordinates, canvas }) => {
  cropResult.value = { coordinates, canvas }
  if (canvas) {
    previewImageUrl.value = canvas.toDataURL('image/jpeg', 0.95)
  }
}

const applyCrop = async () => {
  if (!cropper.value || !cropResult.value) {
    alert('Please select a crop area by adjusting the crop box')
    return
  }

  try {
    const { coordinates, canvas } = cropResult.value

    if (!coordinates || !coordinates.width || !coordinates.height) {
      alert('Please select a crop area')
      return
    }

    imageDimensions.value.cropDimensions = {
      width: coordinates.width,
      height: coordinates.height
    }

    if (canvas) {
      uploadingBanner.value = true

      canvas.toBlob(async (blob) => {
        if (!blob) {
          alert('Failed to create cropped image')
          uploadingBanner.value = false
          return
        }

        try {
          const currentImageUrl = formData.value.image_url || bannerImagePreview.value
          let fileName = selectedFile.value?.name || (currentImageUrl ? currentImageUrl.split('/').pop().split('?')[0] : 'cropped-image.jpg')
          const nameWithoutExt = fileName.replace(/\.[^/.]+$/, '')
          fileName = `${nameWithoutExt}.jpg`

          const formDataUpload = new FormData()
          formDataUpload.append('file', blob, fileName)

          const uploadedUrl = await adminService.uploadBannerImage(formDataUpload)
          if (uploadedUrl) {
            formData.value.image_url = uploadedUrl
            bannerImagePreview.value = uploadedUrl

            if (cropImageSrc.value && cropImageSrc.value.startsWith('blob:')) {
              URL.revokeObjectURL(cropImageSrc.value)
            }

            uploadingBanner.value = false
            showCropModal.value = false
            selectedFile.value = null
            cropResult.value = null
            updatePreview()
          } else {
            throw new Error('No URL returned from server')
          }
        } catch (error) {
          console.error('Failed to upload cropped image:', error)
          const errorMessage = error.response?.data?.error || error.message || 'Failed to upload cropped image. Please try again.'
          alert(errorMessage)
          uploadingBanner.value = false
        }
      }, 'image/jpeg', 0.95)
    } else {
      showCropModal.value = false
      updatePreview()
    }
  } catch (error) {
    console.error('Error applying crop:', error)
    alert('Failed to apply crop. Please try again.')
  }
}

const getPreviewStyle = () => {
  const SIZE = 200
  return {
    width: `${SIZE}px`,
    height: `${SIZE}px`
  }
}

const getImageStyle = () => {
  return {
    width: '100%',
    height: '100%',
    objectFit: 'fill',
    objectPosition: 'center center'
  }
}

const getImageContainerStyle = () => {
  return {
    width: '200px',
    height: '200px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  }
}

const Math = window.Math

const saveBanner = async () => {
  try {
    // First, upload the blob image if there's a selected file that hasn't been uploaded yet
    if (selectedFile.value && bannerImagePreview.value && bannerImagePreview.value.startsWith('blob:')) {
      uploadingBanner.value = true
      try {
        const formDataUpload = new FormData()
        formDataUpload.append('file', selectedFile.value, selectedFile.value.name)
        const uploadedUrl = await adminService.uploadBannerImage(formDataUpload)
        formData.value.image_url = uploadedUrl
        bannerImagePreview.value = uploadedUrl
        selectedFile.value = null
      } catch (error) {
        console.error('Failed to upload image:', error)
        alert('Failed to upload image. Please try again.')
        uploadingBanner.value = false
        return
      } finally {
        uploadingBanner.value = false
      }
    }

    if (!formData.value.image_url || formData.value.image_url.trim() === '') {
      alert('Please upload an image or provide an image URL')
      return
    }

    const bannerData = {
      title: formData.value.title,
      image_url: formData.value.image_url || null,
      link_url: formData.value.link_url || null,
      link_text: formData.value.link_text || null,
      position: formData.value.position,
      pages: formData.value.pages && formData.value.pages.length > 0 ? formData.value.pages : [],
      is_active: formData.value.is_active !== false,
      image_dimensions: {
        cropDimensions: imageDimensions.value.cropDimensions || null
      },
      text_overlay: formData.value.text_overlay || null,
      text_subheading: formData.value.text_subheading || null,
      text_position: formData.value.text_position || null,
      max_height: formData.value.max_height || null,
      text_color: formData.value.text_color || null,
      text_background: formData.value.text_background || null,
      text_font: formData.value.text_font || null,
      text_size_class: formData.value.text_size_class || null
    }

    if (bannerData.image_url && imageDimensions.value.cropDimensions &&
      imageDimensions.value.cropDimensions.width > 0 && imageDimensions.value.cropDimensions.height > 0) {
      try {
        const { resizeImage } = await import('../../utils/imageResize.js')
        const resizedBlob = await resizeImage(
          bannerData.image_url,
          imageDimensions.value.cropDimensions.width,
          imageDimensions.value.cropDimensions.height
        )
        const formDataUpload = new FormData()
        formDataUpload.append('file', resizedBlob, 'resized-image.jpg')
        const resizedUrl = await adminService.uploadBannerImage(formDataUpload)
        bannerData.image_url = resizedUrl
      } catch (error) {
        console.error('Failed to resize image:', error)
      }
    }

    if (props.editingBanner) {
      await adminService.updateBanner(props.editingBanner.id, bannerData)
      alert('Banner updated successfully!')
    } else {
      await adminService.createBanner(bannerData)
      alert('Banner created successfully!')
    }
    emit('preview-updated', null)
    emit('saved')
  } catch (error) {
    console.error('Failed to save banner:', error)
    const errorMessage = error.response?.data?.error || error.message || 'Unknown error'
    alert(`Failed to save banner: ${errorMessage}`)
  }
}
</script>

<style scoped>
input[type="range"] {
  -webkit-appearance: none;
  appearance: none;
}

input[type="range"]::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 18px;
  height: 18px;
  background: #2D4D31;
  cursor: pointer;
  border-radius: 50%;
}

input[type="range"]::-moz-range-thumb {
  width: 18px;
  height: 18px;
  background: #2D4D31;
  cursor: pointer;
  border-radius: 50%;
  border: none;
}

:deep(.ql-editor) {
  min-height: 100px;
  line-height: 1.75;
  color: #253439;
  background-color: #FFFFFF !important;
  direction: ltr !important;
  text-align: left !important;
  unicode-bidi: embed !important;
}

:deep(.ql-container) {
  font-family: 'Manrope', sans-serif;
  background-color: #FFFFFF !important;
  direction: ltr !important;
  unicode-bidi: embed !important;
}

:deep(.ql-toolbar) {
  border-top-left-radius: 0.375rem;
  border-top-right-radius: 0.375rem;
  background-color: #FFFFFF !important;
}

:deep(.ql-editor *) {
  direction: ltr !important;
}

input[type="checkbox"] {
  background-color: #FFFFFF !important;
  appearance: checkbox;
  -webkit-appearance: checkbox;
  -moz-appearance: checkbox;
}

input[type="checkbox"]:checked {
  background-color: #FFFFFF !important;
  accent-color: #2D4D31;
}
</style>
